import { useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useAdminTeam,
  useDeleteTeam,
  useChangeTeamStatus,
  useExtendTeamTrial,
  useChangeTeamPackage,
  useGrantAddon,
  useRevokeAddon,
  useGrantTokens,
  useAdminPackages,
  useEntityAuditLogs,
  useUpdateTeam,
  useAdminAgencies,
  useAdminUsers,
  useAddTeamMember,
  useRemoveTeamMember,
  useChangeMemberRole,
  useTransferTeamOwnership,
} from '@/hooks/useAdmin';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { Badge, Card, CardHeader, CardBody, EmptyState, Modal, ConfirmDialog } from '@/components/ui';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { formatApiError } from '@/services/api';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { Combobox } from '@/components/ui/Combobox';
import type {
  ChangePackageRequest,
  ExtendTrialRequest,
  GrantAddonRequest,
  GrantTokenRequest,
  ChangeStatusRequest,
  AuditLogItem,
  AdminTeamMember,
  TeamRole,
} from '@/types/admin';
import { isLastReviewerCancel } from '@/stores/lastReviewerConfirm.store';
import { isReviewerRole } from '@/utils/releaseRoles';

/**
 * Admin Team Detail Page
 * View and manage individual team details, usage, addons, and members
 */

const STATUS_OPTIONS: Array<'ACTIVE' | 'GRACE_PERIOD' | 'EXPIRED' | 'CANCELLED' | 'SUSPENDED'> = [
  'ACTIVE',
  'GRACE_PERIOD',
  'EXPIRED',
  'CANCELLED',
  'SUSPENDED',
];

const ADDON_TYPES: Array<'WHITELABEL' | 'EXTRA_STORAGE' | 'PRIORITY_SUPPORT'> = [
  'WHITELABEL',
  'EXTRA_STORAGE',
  'PRIORITY_SUPPORT',
];

const TEAM_ROLES: TeamRole[] = ['OWNER', 'ADMIN', 'MEMBER', 'REVIEWER'];

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export const AdminTeamDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatDateTime, formatDateOnly } = useTimezone();
  const teamId = parseInt(id || '0', 10);

  const { data: team, isLoading, error } = useAdminTeam(teamId);
  const deleteTeam = useDeleteTeam();
  const changeStatus = useChangeTeamStatus();
  const extendTrial = useExtendTeamTrial();
  const changePackage = useChangeTeamPackage();
  const grantAddon = useGrantAddon();
  const revokeAddon = useRevokeAddon();
  const grantTokens = useGrantTokens();
  const updateTeam = useUpdateTeam();
  const removeMember = useRemoveTeamMember(teamId);

  // Fetch packages for dropdown
  const { data: packagesData } = useAdminPackages({ isActive: true });
  const allPackages = (packagesData as { data?: { id: number; name: string; isAgency?: boolean }[] })?.data || [];
  const packages = allPackages.filter(pkg => !pkg.isAgency);

  // Agency picker state (debounced server-search, replaces the old static load-all)
  const [agencyInputValue, setAgencyInputValue] = useState('');
  const agencySearchQuery = useDebounce(agencyInputValue, 300);
  const { data: agenciesData, isLoading: isLoadingAgencies } = useAdminAgencies({
    search: agencySearchQuery || undefined,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const agencies = agenciesData?.data || [];

  // ----------- existing inline-edit state -----------

  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<typeof STATUS_OPTIONS[number] | null>(null);
  const [statusReason, setStatusReason] = useState('');

  const [isExtendingTrial, setIsExtendingTrial] = useState(false);
  const [trialDays, setTrialDays] = useState(7);
  const [trialReason, setTrialReason] = useState('');

  const [isChangingPackage, setIsChangingPackage] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [packageReason, setPackageReason] = useState('');

  const [isGrantingAddon, setIsGrantingAddon] = useState(false);
  const [selectedAddonType, setSelectedAddonType] = useState<typeof ADDON_TYPES[number] | null>(null);
  const [addonIsLifetime, setAddonIsLifetime] = useState(false);
  const [addonExpiresAt, setAddonExpiresAt] = useState('');
  const [addonStorageGb, setAddonStorageGb] = useState('');
  const [addonWcSubscriptionId, setAddonWcSubscriptionId] = useState('');

  const [isGrantingTokens, setIsGrantingTokens] = useState(false);
  const [tokenAmount, setTokenAmount] = useState(10000);
  const [tokenExpiresAt, setTokenExpiresAt] = useState('');

  const [revokeAddonDialog, setRevokeAddonDialog] = useState<{ open: boolean; addonId: number | null; addonType: string }>({ open: false, addonId: null, addonType: '' });

  const [deleteDialog, setDeleteDialog] = useState(false);

  // ----------- Team Info inline edit -----------

  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({
    name: '',
    email: '',
    phone: '',
    timezone: '',
    gracePeriodDays: 7,
    agencyId: '',
  });

  const openEditInfo = () => {
    if (!team) return;
    setInfoForm({
      name: team.name,
      email: team.email,
      phone: team.phone ?? '',
      timezone: team.timezone,
      gracePeriodDays: team.gracePeriodDays ?? 7,
      agencyId: team.agencyId != null ? String(team.agencyId) : '',
    });
    // Seed the agency combobox input with the current agency name so it shows
    // correctly even if the agency isn't in the first page of results.
    setAgencyInputValue(team.agencyId != null ? (team.agencyName ?? '') : '');
    setIsEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!team) return;
    if (!infoForm.name.trim()) {
      toast.error('Team name is required');
      return;
    }
    if (!infoForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    try {
      await updateTeam.mutateAsync({
        id: team.id,
        data: {
          name: infoForm.name.trim(),
          email: infoForm.email.trim(),
          phone: infoForm.phone.trim() || null,
          timezone: infoForm.timezone.trim() || team.timezone,
          gracePeriodDays: infoForm.gracePeriodDays,
          agencyId: infoForm.agencyId ? parseInt(infoForm.agencyId, 10) : null,
        },
      });
      toast.success('Team information updated');
      setIsEditingInfo(false);
    } catch {
      toast.error('Failed to update team information');
    }
  };

  // ----------- Features & Settings inline edit -----------

  const [isEditingFeatures, setIsEditingFeatures] = useState(false);
  const [featuresForm, setFeaturesForm] = useState({
    variationsEnabled: false,
    autoTranslateEnabled: false,
    // TODO: variationLanguages and industries chip inputs — follow-up PR
  });

  const openEditFeatures = () => {
    if (!team) return;
    setFeaturesForm({
      variationsEnabled: team.variationsEnabled,
      autoTranslateEnabled: team.autoTranslateEnabled,
    });
    setIsEditingFeatures(true);
  };

  const handleSaveFeatures = async () => {
    if (!team) return;
    try {
      await updateTeam.mutateAsync({
        id: team.id,
        data: {
          variationsEnabled: featuresForm.variationsEnabled,
          autoTranslateEnabled: featuresForm.autoTranslateEnabled,
        },
      });
      toast.success('Features & Settings updated');
      setIsEditingFeatures(false);
    } catch {
      toast.error('Failed to update features');
    }
  };

  // ----------- Member modal state -----------

  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [changeRoleTarget, setChangeRoleTarget] = useState<AdminTeamMember | null>(null);
  const [transferOwnerTarget, setTransferOwnerTarget] = useState<AdminTeamMember | null>(null);
  const [removeMemberDialog, setRemoveMemberDialog] = useState<{ open: boolean; member: AdminTeamMember | null }>({ open: false, member: null });

  const handleRemoveMemberConfirm = async () => {
    if (!removeMemberDialog.member) return;
    const member = removeMemberDialog.member;
    try {
      await removeMember.mutateAsync(member.id);
      toast.success(`${member.fullName || member.email} removed from team`);
      setRemoveMemberDialog({ open: false, member: null });
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error(formatApiError(err).message);
      setRemoveMemberDialog({ open: false, member: null });
    }
  };

  // ----------- Activity History -----------

  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 20;

  const { data: auditLogsData, isLoading: isLoadingHistory, error: historyError } = useEntityAuditLogs(
    'TEAM',
    teamId,
    historyPage,
    historyLimit
  );

  const auditLogs = auditLogsData?.data || [];
  const auditLogsMeta = auditLogsData?.meta;

  const formatActionType = (action: string): string => {
    const actionMap: Record<string, string> = {
      'TEAM_CREATE': 'Team Created',
      'TEAM_UPDATE': 'Team Updated',
      'TEAM_PACKAGE_CHANGE': 'Package Changed',
      'TEAM_TRIAL_EXTEND': 'Trial Extended',
      'TEAM_STATUS_CHANGE': 'Status Changed',
      'TEAM_ADDON_GRANT': 'Addon Granted',
      'TEAM_ADDON_REVOKE': 'Addon Revoked',
      'TEAM_TOKEN_GRANT': 'Tokens Granted',
      'TEAM_DELETE': 'Team Deleted',
    };
    return actionMap[action] || action;
  };

  const formatChanges = (prev: Record<string, unknown> | null, next: Record<string, unknown> | null): string | null => {
    if (!prev || !next) return null;
    const changes: string[] = [];
    const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
    keys.forEach(key => {
      if (prev[key] !== next[key]) changes.push(`${key}: ${prev[key]} → ${next[key]}`);
    });
    return changes.length > 0 ? changes.join(', ') : null;
  };

  const getActionIcon = (action: string): { icon: string; colorClass: string; bgClass: string } => {
    if (action.includes('CREATE') || action.includes('GRANT'))
      return { icon: ICONS.PLUS_CIRCLE, colorClass: 'text-green-600', bgClass: 'bg-green-100' };
    if (action.includes('DELETE') || action.includes('REVOKE'))
      return { icon: ICONS.TRASH, colorClass: 'text-red-600', bgClass: 'bg-red-100' };
    if (action.includes('STATUS'))
      return { icon: ICONS.PULSE, colorClass: 'text-orange-600', bgClass: 'bg-orange-100' };
    return { icon: ICONS.EDIT, colorClass: 'text-primary-700', bgClass: 'bg-primary-50' };
  };

  // ----------- Existing handlers -----------

  const handleDeleteConfirm = async () => {
    if (!team) return;
    try {
      await deleteTeam.mutateAsync(team.id);
      toast.success(`Team "${team.name}" deleted`);
      setDeleteDialog(false);
      navigate('/admin/teams');
    } catch {
      toast.error('Failed to delete team');
    }
  };

  const handleChangeStatus = async () => {
    if (!team || !selectedStatus) return;
    try {
      const data: ChangeStatusRequest = { status: selectedStatus, reason: statusReason || undefined };
      await changeStatus.mutateAsync({ id: team.id, data });
      toast.success(`Status updated to ${selectedStatus}`);
      setIsChangingStatus(false);
      setSelectedStatus(null);
      setStatusReason('');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleExtendTrial = async () => {
    if (!team) return;
    try {
      const data: ExtendTrialRequest = { days: trialDays, reason: trialReason || undefined };
      await extendTrial.mutateAsync({ id: team.id, data });
      toast.success(`Trial extended by ${trialDays} days`);
      setIsExtendingTrial(false);
      setTrialDays(7);
      setTrialReason('');
    } catch {
      toast.error('Failed to extend trial');
    }
  };

  const handleChangePackage = async () => {
    if (!team || !selectedPackageId) return;
    try {
      const data: ChangePackageRequest = { packageId: selectedPackageId, reason: packageReason || undefined };
      await changePackage.mutateAsync({ id: team.id, data });
      toast.success('Package changed successfully');
      setIsChangingPackage(false);
      setSelectedPackageId(null);
      setPackageReason('');
    } catch {
      toast.error('Failed to change package');
    }
  };

  const handleGrantAddon = async () => {
    if (!team || !selectedAddonType) return;
    try {
      const data: GrantAddonRequest = {
        addonType: selectedAddonType,
        isLifetime: addonIsLifetime,
        expiresAt: addonIsLifetime || !addonExpiresAt ? undefined : addonExpiresAt,
        ...(selectedAddonType === 'EXTRA_STORAGE' ? { storageMb: Math.round(parseFloat(addonStorageGb) * 1024) } : {}),
        ...(selectedAddonType === 'EXTRA_STORAGE' && addonWcSubscriptionId.trim() ? { wcSubscriptionId: addonWcSubscriptionId.trim() } : {}),
      };
      await grantAddon.mutateAsync({ id: team.id, data });
      toast.success(`${selectedAddonType} addon granted`);
      setIsGrantingAddon(false);
      setSelectedAddonType(null);
      setAddonIsLifetime(false);
      setAddonExpiresAt('');
      setAddonStorageGb('');
      setAddonWcSubscriptionId('');
    } catch (err) {
      // Server message (409 duplicate subscription, 400 validation, ...) instead of a generic failure.
      toast.error(formatApiError(err).message || 'Failed to grant addon');
    }
  };

  const handleGrantTokens = async () => {
    if (!team) return;
    try {
      const data: GrantTokenRequest = { tokenAmount, expiresAt: tokenExpiresAt || undefined };
      await grantTokens.mutateAsync({ id: team.id, data });
      toast.success(`${tokenAmount.toLocaleString()} tokens granted`);
      setIsGrantingTokens(false);
      setTokenAmount(10000);
      setTokenExpiresAt('');
    } catch {
      toast.error('Failed to grant tokens');
    }
  };

  const handleRevokeAddon = async () => {
    if (!team || !revokeAddonDialog.addonId) return;
    try {
      await revokeAddon.mutateAsync({ teamId: team.id, addonId: revokeAddonDialog.addonId });
      toast.success(`${revokeAddonDialog.addonType} addon revoked`);
      setRevokeAddonDialog({ open: false, addonId: null, addonType: '' });
    } catch {
      toast.error('Failed to revoke addon');
    }
  };

  // Status colors span the full 5-state agency/team lifecycle (incl.
  // SUSPENDED=orange), which falls outside Badge's semantic palette — kept
  // as a local color map rather than forcing into <Badge variant>, matching
  // the same status set on Teams.tsx / AgencyDetail.tsx.
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-700';
      case 'GRACE_PERIOD':
        return 'bg-yellow-100 text-yellow-700';
      case 'EXPIRED':
        return 'bg-red-100 text-red-700';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-700';
      case 'SUSPENDED':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <AdminPageHeader title="Team Details" backTo="/admin/teams" backLabel="Back to Teams" />
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading team: {formatApiError(error).message}
        </div>
      </div>
    );
  }

  if (isLoading || !team) {
    return <div className="p-8 flex justify-center"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <AdminPageHeader
        title={team.name}
        subtitle={team.email}
        backTo="/admin/teams"
        backLabel="Back to Teams"
        leading={
          team.logo ? (
            <img src={team.logo} alt={team.name} className="w-10 h-10 rounded-full object-cover" />
          ) : undefined
        }
        action={
          <Button variant="danger" onClick={() => setDeleteDialog(true)} disabled={deleteTeam.isPending}>
            Delete Team
          </Button>
        }
      />

      {/* ── Team Information ─────────────────────────────────────────────────── */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Team Information</h2>
          {!isEditingInfo && (
            <Button variant="secondary" size="sm" onClick={openEditInfo}>
              <i className={`hm hm-${ICONS.EDIT} mr-2`} />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {!isEditingInfo ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-600">Name</label>
                <p className="mt-1 text-gray-900">{team.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="mt-1 text-gray-900">{team.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Phone</label>
                <p className="mt-1 text-gray-900">{team.phone || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Timezone</label>
                <p className="mt-1 text-gray-900">{team.timezone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Grace Period Days</label>
                <p className="mt-1 text-gray-900">{team.gracePeriodDays ?? 7}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Status</label>
                <div className="mt-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(team.status)}`}>
                    {team.status}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Active</label>
                <div className="mt-1">
                  <Badge variant={team.isActive ? 'success' : 'danger'}>
                    {team.isActive ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Team Type</label>
                <div className="mt-1">
                  <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                    team.teamType === 'agency' ? 'bg-peach text-secondary-700' :
                    team.teamType === 'team' ? 'bg-primary-50 text-primary-800' :
                    team.teamType === 'individual' ? 'bg-teal-100 text-teal-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {team.teamType === 'agency' ? 'Brand Portfolio' :
                     team.teamType === 'team' ? 'Team' :
                     team.teamType === 'individual' ? 'Individual' : 'Not Set'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Created</label>
                <p className="mt-1 text-gray-900">{formatDateTime(team.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Brand Portfolio</label>
                <div className="mt-1 flex items-center gap-2">
                  {team.agencyId ? (
                    <>
                      <span className="text-gray-900">{team.agencyName || 'Unknown'}</span>
                      <Link to={`/admin/agencies/${team.agencyId}`} className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                        View Brand Portfolio
                      </Link>
                    </>
                  ) : (
                    <span className="text-gray-500 text-sm">None (standalone)</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={infoForm.name}
                    onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={infoForm.email}
                    onChange={(e) => setInfoForm({ ...infoForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={infoForm.phone}
                    onChange={(e) => setInfoForm({ ...infoForm, phone: e.target.value })}
                    placeholder="+49 30 …"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <input
                    type="text"
                    value={infoForm.timezone}
                    onChange={(e) => setInfoForm({ ...infoForm, timezone: e.target.value })}
                    placeholder="Europe/Berlin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grace Period Days</label>
                  <input
                    type="number"
                    value={infoForm.gracePeriodDays}
                    onChange={(e) => setInfoForm({ ...infoForm, gracePeriodDays: parseInt(e.target.value, 10) || 0 })}
                    min={0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand Portfolio</label>
                  <Combobox
                    value={agencyInputValue}
                    onChange={(val) => {
                      // User selected a concrete option (numeric ID string)
                      if (val !== '' && !isNaN(parseInt(val, 10))) {
                        setInfoForm({ ...infoForm, agencyId: val });
                        const picked = agencies.find(a => String(a.id) === val);
                        setAgencyInputValue(picked?.name ?? val);
                      } else {
                        // User is typing — update search input and clear selection
                        setAgencyInputValue(val);
                        setInfoForm({ ...infoForm, agencyId: '' });
                      }
                    }}
                    options={[
                      { value: '', label: '(no brand portfolio)' },
                      ...agencies.map(a => ({ value: String(a.id), label: a.name })),
                    ]}
                    placeholder="Search brand portfolio…"
                    loading={isLoadingAgencies}
                    emptyMessage="No matching brand portfolios found"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveInfo} disabled={updateTeam.isPending} loading={updateTeam.isPending}>
                  Save
                </Button>
                <Button variant="secondary" onClick={() => setIsEditingInfo(false)} disabled={updateTeam.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Features & Settings ──────────────────────────────────────────────── */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Features & Settings</h2>
          {!isEditingFeatures && (
            <Button variant="secondary" size="sm" onClick={openEditFeatures}>
              <i className={`hm hm-${ICONS.EDIT} mr-2`} />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {!isEditingFeatures ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-600">Variations Enabled</label>
                <div className="mt-1">
                  <Badge variant={team.variationsEnabled ? 'success' : 'neutral'}>
                    {team.variationsEnabled ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Auto-Translate Enabled</label>
                <div className="mt-1">
                  <Badge variant={team.autoTranslateEnabled ? 'success' : 'neutral'}>
                    {team.autoTranslateEnabled ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Variation Languages</label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {team.variationLanguages && team.variationLanguages.length > 0 ? (
                    team.variationLanguages.map((lang: string) => (
                      <span key={lang} className="px-2 py-0.5 bg-primary-50 text-primary-800 text-xs rounded-full">{lang}</span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">None</span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Industries</label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {team.industries && team.industries.length > 0 ? (
                    team.industries.map((ind: string) => (
                      <span key={ind} className="px-2 py-0.5 bg-peach text-secondary-700 text-xs rounded-full">{ind}</span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">None</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="feat-variations"
                  checked={featuresForm.variationsEnabled}
                  onChange={(e) => setFeaturesForm({ ...featuresForm, variationsEnabled: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
                />
                <label htmlFor="feat-variations" className="text-sm font-medium text-gray-700">Variations Enabled</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="feat-auto-translate"
                  checked={featuresForm.autoTranslateEnabled}
                  onChange={(e) => setFeaturesForm({ ...featuresForm, autoTranslateEnabled: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
                />
                <label htmlFor="feat-auto-translate" className="text-sm font-medium text-gray-700">Auto-Translate Enabled</label>
              </div>
              {/* TODO: variationLanguages and industries chip inputs (follow-up PR) */}
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveFeatures} disabled={updateTeam.isPending} loading={updateTeam.isPending}>
                  Save
                </Button>
                <Button variant="secondary" onClick={() => setIsEditingFeatures(false)} disabled={updateTeam.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Package & Subscription ───────────────────────────────────────────── */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Package & Subscription</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">Current Package</label>
              <p className="mt-1">
                <span className="px-3 py-1.5 bg-primary-50 text-primary-800 text-sm rounded-md font-medium">
                  {team.packageName || 'No package'}
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Expires At</label>
              <p className="mt-1 text-gray-900">{team.expiresAt ? formatDateOnly(team.expiresAt) : 'Never'}</p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            {!isChangingPackage ? (
              <Button variant="secondary" onClick={() => setIsChangingPackage(true)}>
                <i className={`hm hm-${ICONS.EDIT} mr-2`} />
                Change Package
              </Button>
            ) : (
              <div className="space-y-3 w-full">
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">New Package</label>
                  <Select
                    value={selectedPackageId?.toString() || team.packageId?.toString() || ''}
                    onValueChange={(value) => setSelectedPackageId(value ? parseInt(value, 10) : null)}
                    placeholder="Select a package..."
                    options={packages.map((pkg) => ({
                      value: pkg.id.toString(),
                      label: `${pkg.name}${pkg.id === team.packageId ? ' (current)' : ''}`
                    }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">Reason (optional)</label>
                  <input
                    type="text"
                    value={packageReason}
                    onChange={(e) => setPackageReason(e.target.value)}
                    placeholder="Reason for change"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleChangePackage} disabled={!selectedPackageId || changePackage.isPending} loading={changePackage.isPending}>
                    Save Package
                  </Button>
                  <Button variant="secondary" onClick={() => { setIsChangingPackage(false); setSelectedPackageId(null); setPackageReason(''); }} disabled={changePackage.isPending}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {!isExtendingTrial && !isChangingPackage && (
              <Button variant="secondary" onClick={() => setIsExtendingTrial(true)}>
                <i className={`hm hm-${ICONS.CLOCK} mr-2`} />
                Extend Expiry
              </Button>
            )}
          </div>

          {isExtendingTrial && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Days to Extend</label>
                <input
                  type="number"
                  value={trialDays}
                  onChange={(e) => setTrialDays(parseInt(e.target.value, 10))}
                  min="1"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Reason (optional)</label>
                <input
                  type="text"
                  value={trialReason}
                  onChange={(e) => setTrialReason(e.target.value)}
                  placeholder="Reason for extension"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleExtendTrial} disabled={trialDays < 1 || extendTrial.isPending} loading={extendTrial.isPending}>
                  Extend Expiry
                </Button>
                <Button variant="secondary" onClick={() => { setIsExtendingTrial(false); setTrialDays(7); setTrialReason(''); }} disabled={extendTrial.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Status Management ────────────────────────────────────────────────── */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Status Management</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          {!isChangingStatus ? (
            <Button variant="secondary" onClick={() => setIsChangingStatus(true)}>
              <i className={`hm hm-${ICONS.TOOLS} mr-2`} />
              Change Status
            </Button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">New Status</label>
                <Select
                  value={selectedStatus || team.status}
                  onValueChange={(value) => setSelectedStatus(value as typeof STATUS_OPTIONS[number])}
                  options={STATUS_OPTIONS.map(status => ({ value: status, label: status }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Reason (optional)</label>
                <input
                  type="text"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Reason for status change"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleChangeStatus} disabled={!selectedStatus || selectedStatus === team.status || changeStatus.isPending} loading={changeStatus.isPending}>
                  Save Status
                </Button>
                <Button variant="secondary" onClick={() => { setIsChangingStatus(false); setSelectedStatus(null); setStatusReason(''); }} disabled={changeStatus.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Usage Statistics ─────────────────────────────────────────────────── */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Usage Statistics</h2>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">Posts This Month</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {team.usage.postsThisMonth.toLocaleString()}
                {team.usage.limits.maxPostsPerMonth && <span className="text-sm text-gray-500 ml-2">/ {team.usage.limits.maxPostsPerMonth.toLocaleString()}</span>}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">AI Tokens Used</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {team.usage.aiTokensUsed.toLocaleString()}
                {team.usage.limits.maxAITokens !== null && <span className="text-sm text-gray-500 ml-2">/ {(team.usage.aiTokensRemaining !== undefined
                  ? team.usage.aiTokensUsed + team.usage.aiTokensRemaining
                  : team.usage.limits.maxAITokens + team.usage.aiTokensAddonRemaining).toLocaleString()}</span>}
              </p>
              {team.usage.aiTokensAddonRemaining > 0 && team.usage.limits.maxAITokens !== null && (
                <p className="text-xs text-gray-500 mt-1">
                  {team.usage.limits.maxAITokens.toLocaleString()} monthly + {team.usage.aiTokensAddonRemaining.toLocaleString()} addon balance
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Storage Used (MB)</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {team.usage.storageUsedMb.toFixed(2)}
                {team.usage.limits.maxStorage && <span className="text-sm text-gray-500 ml-2">/ {team.usage.limits.maxStorage.toLocaleString()}</span>}
              </p>
              {team.storagePool && (
                <p className="text-xs text-gray-500 mt-1">
                  Brand Portfolio pool: this brand uses {team.storagePool.brandPoolUsedMb.toLocaleString()} MB of the pool ({team.storagePool.poolUsedMb.toLocaleString()} of {team.storagePool.poolMb.toLocaleString()} MB used by all brands)
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Calendars</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {team.usage.calendarsCount}
                {team.usage.limits.maxCalendars && <span className="text-sm text-gray-500 ml-2">/ {team.usage.limits.maxCalendars}</span>}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Variations</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {team.usage.variationsCount}
                {team.usage.limits.maxVariations && <span className="text-sm text-gray-500 ml-2">/ {team.usage.limits.maxVariations}</span>}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Channels</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {team.channelCount}
                {team.usage.limits.maxChannels && <span className="text-sm text-gray-500 ml-2">/ {team.usage.limits.maxChannels}</span>}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Users</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {team.userCount}
                {team.usage.limits.maxUsers && <span className="text-sm text-gray-500 ml-2">/ {team.usage.limits.maxUsers}</span>}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Total Posts</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{team.postCount.toLocaleString()}</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Addons ───────────────────────────────────────────────────────────── */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Addons</h2>
          {!isGrantingAddon && !isGrantingTokens && (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setIsGrantingAddon(true)}>
                <i className={`hm hm-${ICONS.PLUS} mr-2`} />
                Grant Addon
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setIsGrantingTokens(true)}>
                <i className={`hm hm-${ICONS.SPARK} mr-2`} />
                Grant Tokens
              </Button>
            </div>
          )}
        </CardHeader>
        <CardBody className="space-y-4">
          {team.addons.length === 0 ? (
            <EmptyState title="No addons granted" />
          ) : (
            <div className="space-y-2">
              {team.addons.map((addon) => (
                <div key={addon.id} className="flex items-center justify-between py-3 px-4 border border-gray-200 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">
                      {addon.addonType}
                      {addon.addonType === 'EXTRA_STORAGE' && (
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          {addon.storageMb ? `+${(addon.storageMb / 1024).toLocaleString()} GB` : 'no amount (inactive)'}
                          {addon.purchaseId ? ` · WC subscription #${addon.purchaseId}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {addon.isLifetime ? <span className="text-green-600 font-medium">Lifetime</span> : <>Expires: {addon.expiresAt ? formatDateOnly(addon.expiresAt) : 'Never'}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={addon.isActive ? 'success' : 'danger'}>
                      {addon.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button size="sm" variant="danger" onClick={() => setRevokeAddonDialog({ open: true, addonId: addon.id, addonType: addon.addonType })}>
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isGrantingAddon && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Addon Type</label>
                <Select
                  value={selectedAddonType || ''}
                  onValueChange={(value) => setSelectedAddonType(value as typeof ADDON_TYPES[number])}
                  placeholder="Select addon type"
                  options={ADDON_TYPES.map(type => ({ value: type, label: type }))}
                />
              </div>
              {selectedAddonType === 'EXTRA_STORAGE' && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">Extra storage (GB)</label>
                  <input type="number" value={addonStorageGb} onChange={(e) => setAddonStorageGb(e.target.value)} min="1" step="1" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400" />
                  <label className="text-sm font-medium text-gray-600 block mt-3 mb-2">WooCommerce subscription id (optional, needs an end date)</label>
                  <input type="text" inputMode="numeric" value={addonWcSubscriptionId} onChange={(e) => setAddonWcSubscriptionId(e.target.value.replace(/\D/g, '').replace(/^0+/, ''))} placeholder="e.g. 14200" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="addonLifetime"
                  checked={addonIsLifetime}
                  onChange={(e) => setAddonIsLifetime(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
                />
                <label htmlFor="addonLifetime" className="text-sm font-medium text-gray-600">Lifetime addon</label>
              </div>
              {!addonIsLifetime && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">Expires At {selectedAddonType === 'EXTRA_STORAGE' ? '(required)' : '(optional)'}</label>
                  <DatePickerSingle value={addonExpiresAt} onChange={(iso) => setAddonExpiresAt(iso || '')} placeholder="dd.mm.yyyy" minDate={new Date()} />
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={handleGrantAddon} disabled={!selectedAddonType || (selectedAddonType === 'EXTRA_STORAGE' && (!(parseFloat(addonStorageGb) >= 1) || (!addonIsLifetime && !addonExpiresAt) || (addonWcSubscriptionId !== '' && addonIsLifetime))) || grantAddon.isPending} loading={grantAddon.isPending}>Grant Addon</Button>
                <Button variant="secondary" onClick={() => { setIsGrantingAddon(false); setSelectedAddonType(null); setAddonIsLifetime(false); setAddonExpiresAt(''); setAddonStorageGb(''); setAddonWcSubscriptionId(''); }} disabled={grantAddon.isPending}>Cancel</Button>
              </div>
            </div>
          )}

          {isGrantingTokens && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Token Amount</label>
                <input type="number" value={tokenAmount} onChange={(e) => setTokenAmount(parseInt(e.target.value, 10))} min="1000" step="1000" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Expires At (optional)</label>
                <DatePickerSingle value={tokenExpiresAt} onChange={(iso) => setTokenExpiresAt(iso || '')} placeholder="dd.mm.yyyy" minDate={new Date()} />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleGrantTokens} disabled={tokenAmount < 1000 || grantTokens.isPending} loading={grantTokens.isPending}>Grant Tokens</Button>
                <Button variant="secondary" onClick={() => { setIsGrantingTokens(false); setTokenAmount(10000); setTokenExpiresAt(''); }} disabled={grantTokens.isPending}>Cancel</Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Team Members ─────────────────────────────────────────────────────── */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Team Members ({team.users.length})</h2>
          <Button variant="secondary" size="sm" onClick={() => setShowAddMemberModal(true)}>
            <i className={`hm hm-${ICONS.PLUS} mr-2`} />
            Add Member
          </Button>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {team.users.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState title="No users found" />
                  </td>
                </tr>
              ) : (
                team.users.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{member.fullName || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="info">{member.role}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* 4-way categorical role pill — kept as a raw class map since
                          Badge's 6 variants can't preserve all 4 distinct hues here. */}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        member.teamRole === 'OWNER' ? 'bg-peach text-secondary-700' :
                        member.teamRole === 'ADMIN' ? 'bg-soft-mint text-secondary-700' :
                        member.teamRole === 'MEMBER' ? 'bg-gray-100 text-gray-700' :
                        isReviewerRole(member.teamRole) ? 'bg-teal-100 text-teal-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {member.teamRole ?? '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Link to={`/admin/users/${member.id}`} className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                          View
                        </Link>
                        {/* Change Role — hidden for OWNER to avoid accidental last-owner demotion */}
                        {member.teamRole !== 'OWNER' && (
                          <button
                            type="button"
                            title="Change Role"
                            aria-label={`Change role of ${member.fullName || member.email}`}
                            onClick={() => setChangeRoleTarget(member)}
                            className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                          >
                            <i className={`hm hm-${ICONS.EDIT} text-xs`} />
                          </button>
                        )}
                        {/* Make Owner — hidden for current OWNER */}
                        {member.teamRole !== 'OWNER' && (
                          <button
                            type="button"
                            title="Make Owner"
                            aria-label={`Transfer ownership to ${member.fullName || member.email}`}
                            onClick={() => setTransferOwnerTarget(member)}
                            className="p-1 rounded text-gray-500 hover:text-yellow-600 hover:bg-yellow-50"
                          >
                            <i className={`hm hm-${ICONS.STAR} text-xs`} />
                          </button>
                        )}
                        {/* Remove — always visible; backend blocks last-owner removal */}
                        <button
                          type="button"
                          title="Remove Member"
                          aria-label={`Remove ${member.fullName || member.email} from team`}
                          onClick={() => setRemoveMemberDialog({ open: true, member })}
                          className="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <i className={`hm hm-${ICONS.TRASH} text-xs`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Activity History ─────────────────────────────────────────────────── */}
      <Card padding="none">
        <button
          onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
          className="w-full px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Activity History</h2>
            {auditLogsMeta && (
              // Deliberately darker than Badge's neutral (bg-gray-100) to stand out
              // against this header's own bg-gray-50 — kept as a raw pill.
              <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">{auditLogsMeta.total}</span>
            )}
          </div>
          <i className={`hm hm-${isHistoryExpanded ? ICONS.ANGLE_UP : ICONS.ANGLE_DOWN} text-gray-600`} />
        </button>

        {isHistoryExpanded && (
          <div className="p-6">
            {isLoadingHistory ? (
              <div className="flex justify-center py-8"><LoadingSpinner /></div>
            ) : historyError ? (
              <div className="text-center py-8 text-red-600">Failed to load activity history</div>
            ) : auditLogs.length === 0 ? (
              <EmptyState title="No activity history available" />
            ) : (
              <div className="space-y-6">
                <div className="space-y-0">
                  {auditLogs.map((log: AuditLogItem, index: number) => {
                    const { icon, colorClass, bgClass } = getActionIcon(log.action);
                    const changesText = formatChanges(log.previousValues, log.newValues);
                    const reason = log.metadata?.reason ? String(log.metadata.reason) : null;
                    return (
                      <div key={log.id} className={`relative pl-8 pb-6 ${index !== auditLogs.length - 1 ? 'border-l-2 border-gray-200' : ''}`}>
                        <div className={`absolute -left-4 top-0 w-8 h-8 rounded-full ${bgClass} flex items-center justify-center`}>
                          <i className={`hm hm-${icon} ${colorClass} text-sm`} />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{formatActionType(log.action)}</span>
                            <span className="text-sm text-gray-500">{formatDateTime(log.createdAt)}</span>
                          </div>
                          <div className="text-sm text-gray-600">by {log.adminName || log.adminEmail}</div>
                          {reason && <div className="text-sm text-gray-500 italic">Reason: {reason}</div>}
                          {changesText && <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded mt-2">{changesText}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {auditLogsMeta && auditLogsMeta.totalPages > 1 && (
                  <div className="border-t pt-4 flex items-center justify-between">
                    <div className="text-sm text-gray-600">Showing {auditLogs.length} of {auditLogsMeta.total}</div>
                    {historyPage < auditLogsMeta.totalPages && (
                      <Button variant="secondary" size="sm" onClick={() => setHistoryPage(historyPage + 1)}>Load More</Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ── Dialogs ──────────────────────────────────────────────────────────── */}

      <ConfirmDialog
        open={deleteDialog}
        onOpenChange={setDeleteDialog}
        title="Delete Team"
        description={`Are you sure you want to delete "${team.name}"?\n\nThis will delete all posts, channels, and users associated with this team.\n\nThis action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteTeam.isPending}
      />

      <ConfirmDialog
        open={revokeAddonDialog.open}
        onOpenChange={(open) => !open && setRevokeAddonDialog({ open: false, addonId: null, addonType: '' })}
        title="Revoke Addon"
        description={`Are you sure you want to revoke the ${revokeAddonDialog.addonType} addon? This will remove the addon from this team.`}
        confirmText="Revoke"
        variant="danger"
        onConfirm={handleRevokeAddon}
        loading={revokeAddon.isPending}
      />

      <ConfirmDialog
        open={removeMemberDialog.open}
        onOpenChange={(open) => !open && setRemoveMemberDialog({ open: false, member: null })}
        title="Remove Team Member"
        description={`Remove ${(removeMemberDialog.member?.fullName || removeMemberDialog.member?.email) ?? 'this user'} from this team? They will lose access to all team content.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleRemoveMemberConfirm}
        loading={removeMember.isPending}
      />

      {/* ── Local modals ─────────────────────────────────────────────────────── */}

      {showAddMemberModal && (
        <AddTeamMemberModal
          teamId={teamId}
          existingMemberIds={team.users.map(u => u.id)}
          onClose={() => setShowAddMemberModal(false)}
        />
      )}

      {changeRoleTarget && (
        <ChangeMemberRoleModal
          teamId={teamId}
          member={changeRoleTarget}
          onClose={() => setChangeRoleTarget(null)}
        />
      )}

      {transferOwnerTarget && (
        <TransferOwnershipModal
          teamId={teamId}
          member={transferOwnerTarget}
          currentOwnerName={team.users.find(u => u.teamRole === 'OWNER')?.fullName ?? 'current owner(s)'}
          onClose={() => setTransferOwnerTarget(null)}
        />
      )}
    </div>
  );
};

// ============================================================================
// LOCAL MODAL: Add Team Member
// ============================================================================

interface AddTeamMemberModalProps {
  teamId: number;
  existingMemberIds: number[];
  onClose: () => void;
}

const AddTeamMemberModal = ({ teamId, existingMemberIds, onClose }: AddTeamMemberModalProps) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<TeamRole>('MEMBER');
  const [inputValue, setInputValue] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Debounce the raw input so we only hit the API after the user stops typing.
  const searchQuery = useDebounce(inputValue, 300);

  const { data: usersData, isLoading: isLoadingUsers } = useAdminUsers({
    search: searchQuery || undefined,
    limit: 50,
    isActive: true,
  });

  const addMember = useAddTeamMember(teamId);

  const availableUsers = (usersData?.data ?? []).filter(u => !existingMemberIds.includes(u.id));

  const comboboxOptions = availableUsers.map(u => ({
    value: String(u.id),
    label: u.fullName ? `${u.fullName} (${u.email})` : u.email,
  }));

  // Drive search from combobox input changes — Combobox fires onChange on every
  // keystroke. We track the raw value in inputValue (debounced → searchQuery for
  // the API) and only set selectedUserId when the value resolves to a numeric ID
  // (i.e. the user actually picked an option from the dropdown).
  const handleComboboxChange = (val: string) => {
    setInlineError(null);
    if (!isNaN(parseInt(val, 10))) {
      // User selected a concrete option — store the ID and set the display label.
      setSelectedUserId(val);
      const picked = comboboxOptions.find(o => o.value === val);
      setInputValue(picked?.label ?? val);
    } else {
      // User is still typing — update the search input, clear any prior selection.
      setSelectedUserId('');
      setInputValue(val);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    if (!selectedUserId) return;

    const parsedId = parseInt(selectedUserId, 10);
    if (isNaN(parsedId)) {
      setInlineError('Please select a user from the list.');
      return;
    }

    try {
      await addMember.mutateAsync({ userId: parsedId, teamRole: selectedRole });
      toast.success('Member added to team');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('409') || msg.toLowerCase().includes('already')) {
        setInlineError('This user is already a member of this team.');
      } else {
        toast.error('Failed to add member');
      }
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Add Team Member"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={addMember.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="add-team-member-form" disabled={!selectedUserId || addMember.isPending} loading={addMember.isPending}>
            Add Member
          </Button>
        </ModalFooter>
      }
    >
      <form id="add-team-member-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
          <Combobox
            value={inputValue}
            onChange={handleComboboxChange}
            options={comboboxOptions}
            placeholder="Search by name or email…"
            loading={isLoadingUsers}
            emptyMessage="No matching users (all may already be members)"
          />
          <p className="text-xs text-gray-500 mt-1">Only users not already on this team are shown.</p>
        </div>

        <Select
          label="Team Role"
          value={selectedRole}
          onValueChange={(v) => setSelectedRole(v as TeamRole)}
          options={TEAM_ROLES.map(r => ({ value: r, label: r }))}
        />

        {inlineError && <p className="text-sm text-red-600">{inlineError}</p>}
      </form>
    </Modal>
  );
};

// ============================================================================
// LOCAL MODAL: Change Member Role
// ============================================================================

interface ChangeMemberRoleModalProps {
  teamId: number;
  member: AdminTeamMember;
  onClose: () => void;
}

const ChangeMemberRoleModal = ({ teamId, member, onClose }: ChangeMemberRoleModalProps) => {
  const [selectedRole, setSelectedRole] = useState<TeamRole>(member.teamRole ?? 'MEMBER');
  const changeMemberRole = useChangeMemberRole(teamId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await changeMemberRole.mutateAsync({ userId: member.id, teamRole: selectedRole });
      toast.success(`Role updated to ${selectedRole}`);
      onClose();
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error(formatApiError(err).message);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Change Team Role"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={changeMemberRole.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="change-member-role-form" disabled={selectedRole === member.teamRole || changeMemberRole.isPending} loading={changeMemberRole.isPending}>
            Save Role
          </Button>
        </ModalFooter>
      }
    >
      <p className="text-sm text-gray-500 mb-4">{member.fullName || member.email}</p>

      <form id="change-member-role-form" onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Team Role"
          value={selectedRole}
          onValueChange={(v) => setSelectedRole(v as TeamRole)}
          options={TEAM_ROLES.filter(r => r !== 'OWNER').map(r => ({ value: r, label: r }))}
        />
      </form>
    </Modal>
  );
};

// ============================================================================
// LOCAL MODAL: Transfer Ownership
// ============================================================================

interface TransferOwnershipModalProps {
  teamId: number;
  member: AdminTeamMember;
  currentOwnerName: string;
  onClose: () => void;
}

const TransferOwnershipModal = ({ teamId, member, currentOwnerName, onClose }: TransferOwnershipModalProps) => {
  const transferOwnership = useTransferTeamOwnership(teamId);

  const handleConfirm = async () => {
    try {
      await transferOwnership.mutateAsync({ newOwnerUserId: member.id });
      toast.success(`Ownership transferred to ${member.fullName || member.email}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err).message);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Transfer Ownership"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={transferOwnership.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={transferOwnership.isPending} loading={transferOwnership.isPending}>
            Transfer Ownership
          </Button>
        </ModalFooter>
      }
    >
      <p className="text-sm text-gray-600 mb-2">
        Transfer ownership to <strong>{member.fullName || member.email}</strong>?
        {currentOwnerName && <> The {currentOwnerName} will be demoted to ADMIN.</>}
      </p>
      <p className="text-xs text-amber-600">This action changes who controls this team.</p>
    </Modal>
  );
};

export default AdminTeamDetail;
